import ApplicationActions from "../components/applications/ApplicationActions";
import ApplicationHeader from "../components/applications/ApplicationHeader";
import ApplicationInfoSection from "../components/applications/ApplicationInfoSection";
import ApplicationPaymentsPanel from "../components/applications/ApplicationPaymentsPanel";
import CategoriesList from "../components/applications/CategoriesList";
import DeletePresentationDialog from "../components/applications/DeletePresentationDialog";
import EmptyState from "../components/shared/EmptyState";
import Loading from "../components/core/Loading";
import { Alert, Box, Snackbar } from "@mui/material";
import { ApplicationStatus } from "../interfaces/enums";
import { ApplicationWithId } from "../interfaces/IApplications";
import {
    Check,
    MonetizationOn,
    PictureAsPdf,
    Search,
    Undo
    } from "@mui/icons-material";
import { compareCategoriesForDisplay } from "../utils/categorySort";
import { isRegistrationOpen } from "../utils/utils";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { useApplicationContext } from "../contexts/ApplicationContext";
import { useContestCategoriesContext } from "../contexts/ContestCategoriesContext";
import { useContestContext } from "../contexts/ContestContext";
import {
    useEffect,
    useMemo,
    useRef,
    useState
    } from "react";
import { useNavigationHelper } from "../hooks/useNavigationHelper";
import { usePDF } from "../hooks/usePDF";
import { usePresentationsContext } from "../contexts/PresentationsContext";
import { useUserContext } from "../contexts/UserContext";

export default function ApplicationDetails() {
  const { application, refreshApplication, updateApplicationStatus, updateApplication } = useApplicationContext();
  const { contest } = useContestContext();
  const { categories } = useContestCategoriesContext();
  const {
    presentations,
    isLoading: presentationsLoading,
    handlePresentationDelete,
  } = usePresentationsContext();
  const { user } = useUserContext();
  const { goToNewPresentation, goToApplicationPaymentsDetails, goToPresentationEdit } =
    useNavigationHelper();
  // Przełącznik widoku: kategorie vs płatności (inline)
  const [showPayments, setShowPayments] = useState(false);
  const { isGenerating, error, generateApplicationConfirmation, clearError } = usePDF();
  const location = useLocation();
  const navigate = useNavigate();
  const [approveError, setApproveError] = useState<string | null>(null);
  const [lockApproval, setLockApproval] = useState(false);

  // Stan dla wyszukiwania
  const [searchTerm, setSearchTerm] = useState("");
  // Stan dla globalnego zwijania kategorii
  const [allCollapsed, setAllCollapsed] = useState(false);
  // Stan dla filtrowania per dzień - domyślnie wszystkie dni odznaczone (pokazuj wszystkie)
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  // Stan dla filtrowania kategorii z prezentacjami
  // Domyślnie false dla DRAFT, true dla innych statusów
  const [showOnlyWithPresentations, setShowOnlyWithPresentations] = useState(
    application?.status === ApplicationStatus.DRAFT ? false : true,
  );
  // TEMPORARILY DISABLED FOR PROD - Music functionality
  // Stan dla filtrowania kategorii z muzyką własną
  // const [showOnlyWithCustomMusic, setShowOnlyWithCustomMusic] = useState(false);
  const [showOnlyWithCustomMusic] = useState(false);

  // Paginacja kategorii
  const [categoriesPage, setCategoriesPage] = useState(0);
  const [categoriesPerPage, setCategoriesPerPage] = useState(15);
  // Stan dla dialogu potwierdzenia usunięcia
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [presentationToDelete, setPresentationToDelete] = useState<any>(null);
  // Stan dla powiadomienia o sukcesie PDF
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const activeCategories = categories.filter((cat) => cat.isActive === true);

  // Ref do śledzenia, czy przywracamy stan z location.state (aby nie resetować strony)
  const isRestoringState = useRef(false);

  // Automatyczne ustawienie switcha w zależności od statusu aplikacji
  useEffect(() => {
    if (application) {
      // Jeśli status nie jest DRAFT, ustaw switch na true
      if (application.status !== ApplicationStatus.DRAFT) {
        setShowOnlyWithPresentations(true);
      } else {
        // Dla DRAFT ustaw na false tylko jeśli nie przywracamy stanu
        if (!isRestoringState.current) {
          setShowOnlyWithPresentations(false);
        }
      }
    }
  }, [application?.status]);

  // Przywracanie stanu z location.state po powrocie z formularza
  useEffect(() => {
    if (location.state) {
      isRestoringState.current = true;
      const {
        page,
        itemsPerPage,
        searchTerm: savedSearchTerm,
        selectedDays: savedSelectedDays,
        showOnlyWithPresentations: savedShowOnlyWithPresentations,
        categoryId,
      } = location.state;

      // Przywróć stan paginacji i filtrów
      if (page !== undefined) setCategoriesPage(page);
      if (itemsPerPage !== undefined) setCategoriesPerPage(itemsPerPage);
      if (savedSearchTerm !== undefined) setSearchTerm(savedSearchTerm);
      if (savedSelectedDays !== undefined) setSelectedDays(savedSelectedDays);
      // Przywróć switch tylko jeśli status jest DRAFT (dla innych statusów zawsze true)
      if (
        savedShowOnlyWithPresentations !== undefined &&
        application?.status === ApplicationStatus.DRAFT
      ) {
        setShowOnlyWithPresentations(savedShowOnlyWithPresentations);
      }

      // Wyczyść state, aby nie przywracać go przy każdym renderze
      navigate(location.pathname, { replace: true, state: null });

      // Opcjonalnie: scroll do kategorii po załadowaniu
      if (categoryId) {
        setTimeout(() => {
          const element = document.getElementById(`category-${categoryId}`);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }, 10);
      }

      // Reset flagi po krótkim czasie
      setTimeout(() => {
        isRestoringState.current = false;
      }, 100);
    }
  }, [location.state, location.pathname, navigate, application?.status]);

  // Pobierz unikalne dni z aktywnych kategorii
  const availableDays = useMemo(() => {
    const days = activeCategories.map((cat) => cat.day);
    return Array.from(new Set(days)).sort((a, b) => a - b);
  }, [activeCategories]);

  // Funkcja pomocnicza do sprawdzania czy prezentacja pasuje do wyszukiwania
  const isPresentationMatchingSearch = (presentation: any, searchLower: string) => {
    // Wyszukiwanie po nazwie prezentacji
    const nameMatch = presentation.name.toLowerCase().includes(searchLower);

    // Wyszukiwanie po tytule prezentacji
    const titleMatch = presentation.title?.toLowerCase().includes(searchLower) || false;

    // Wyszukiwanie po imionach i nazwiskach uczestników
    const participantMatch =
      presentation.participants?.some((participant: any) => {
        const firstNameMatch = participant.firstName.toLowerCase().includes(searchLower);
        const lastNameMatch = participant.lastName.toLowerCase().includes(searchLower);
        const fullNameMatch = `${participant.firstName} ${participant.lastName}`
          .toLowerCase()
          .includes(searchLower);
        return firstNameMatch || lastNameMatch || fullNameMatch;
      }) || false;

    return nameMatch || titleMatch || participantMatch;
  };

  // Filtrowanie i sortowanie kategorii alfabetycznie
  const filteredCategories = useMemo(() => {
    let filtered = activeCategories;

    // Filtrowanie na podstawie wybranych dni (tylko jeśli jakieś dni są wybrane)
    if (selectedDays.length > 0) {
      filtered = filtered.filter((category) => selectedDays.includes(category.day));
    }

    // Filtrowanie kategorii z prezentacjami
    if (showOnlyWithPresentations) {
      filtered = filtered.filter((category) => {
        const categoryPresentations =
          presentations?.filter((p) => p.categoryId === category.id) || [];
        return categoryPresentations.length > 0;
      });
    }

    // TEMPORARILY DISABLED FOR PROD - Music functionality
    // Filtrowanie kategorii z muzyką własną
    // Pokazuj tylko kategorie, które pozwalają na własną muzykę (allowCustomMusic === true)
    // if (showOnlyWithCustomMusic) {
    //   filtered = filtered.filter((category) => category.allowCustomMusic === true);
    // }

    // Filtrowanie na podstawie wyszukiwania
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();

      filtered = filtered.filter((category) => {
        // Wyszukiwanie po nazwie i typie kategorii
        const categoryMatch =
          category.name.toLowerCase().includes(searchLower) ||
          category.type.toLowerCase().includes(searchLower);

        // Wyszukiwanie po prezentacjach w kategorii
        const categoryPresentations =
          presentations?.filter((p) => p.categoryId === category.id) || [];
        const presentationMatch = categoryPresentations.some((presentation) =>
          isPresentationMatchingSearch(presentation, searchLower),
        );

        return categoryMatch || presentationMatch;
      });
    }

    // Sortowanie zgodne z adminem: dzień -> typ -> rozmiar -> nazwa
    return filtered.sort(compareCategoriesForDisplay);
  }, [activeCategories, selectedDays, searchTerm, showOnlyWithPresentations, showOnlyWithCustomMusic, presentations]);

  // Reset strony do 0, gdy zmieniają się filtry/wyszukiwanie (ale nie podczas przywracania stanu)
  useEffect(() => {
    // Nie resetuj strony, jeśli przywracamy stan z location.state
    if (isRestoringState.current) {
      return;
    }

    // Resetuj stronę do 0, gdy zmieniają się filtry
    setCategoriesPage(0);
  }, [searchTerm, selectedDays, showOnlyWithPresentations, showOnlyWithCustomMusic]);

  // Sprawdź, czy po przywróceniu stanu strona jest poprawna (czy są wyniki na tej stronie)
  useEffect(() => {
    if (isRestoringState.current && filteredCategories.length > 0) {
      const maxPage = Math.ceil(filteredCategories.length / categoriesPerPage) - 1;
      if (categoriesPage > maxPage) {
        // Jeśli strona jest poza zakresem, zresetuj do 0
        setCategoriesPage(0);
      }
    }
  }, [filteredCategories.length, categoriesPerPage, categoriesPage]);

  // Paginacja kategorii
  const paginatedCategories = useMemo(() => {
    const start = categoriesPage * categoriesPerPage;
    const end = start + categoriesPerPage;
    return filteredCategories.slice(start, end);
  }, [filteredCategories, categoriesPage, categoriesPerPage]);

  // Filtrowanie prezentacji na podstawie wyszukiwania
  const filteredPresentations = useMemo(() => {
    if (!searchTerm || !presentations) {
      return presentations || [];
    }

    const searchLower = searchTerm.toLowerCase();

    return presentations.filter((presentation) => {
      // Znajdź kategorię dla tej prezentacji
      const category = activeCategories.find((cat) => cat.id === presentation.categoryId);
      if (!category) return false;

      // Jeśli kategoria pasuje do wyszukiwania (nazwa lub typ), pokaż wszystkie prezentacje z tej kategorii
      const categoryMatch =
        category.name.toLowerCase().includes(searchLower) ||
        category.type.toLowerCase().includes(searchLower);

      if (categoryMatch) {
        return true;
      }

      // W przeciwnym razie pokaż tylko prezentacje pasujące do wyszukiwania
      return isPresentationMatchingSearch(presentation, searchLower);
    });
  }, [presentations, searchTerm, activeCategories]);

  const handleAddPresentation = (categoryId: string) => {
    if (application) {
      // Przekaż aktualny stan do formularza
      const state = {
        page: categoriesPage,
        itemsPerPage: categoriesPerPage,
        searchTerm,
        selectedDays,
        showOnlyWithPresentations,
        categoryId,
      };
      goToNewPresentation(application.contestId, application.id, categoryId, state);
    }
  };

  const handleEditPresentation = (presentation: any) => {
    if (application) {
      // Przekaż aktualny stan do formularza
      const state = {
        page: categoriesPage,
        itemsPerPage: categoriesPerPage,
        searchTerm,
        selectedDays,
        showOnlyWithPresentations,
        categoryId: presentation.categoryId,
      };
      goToPresentationEdit(
        application.contestId,
        application.id,
        presentation.categoryId,
        presentation.id,
        state,
      );
    }
  };

  const handleDeletePresentation = (presentation: any) => {
    setPresentationToDelete(presentation);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (presentationToDelete) {
      const result = await handlePresentationDelete(presentationToDelete.id);
      if (result.success) {
        setDeleteDialogOpen(false);
        setPresentationToDelete(null);
      } else {
        console.error("Błąd podczas usuwania prezentacji:", result.error);
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setPresentationToDelete(null);
  };

  // Funkcja do zwijania wszystkich kategorii
  const handleCollapseAll = () => {
    setAllCollapsed(!allCollapsed);
  };

  // Logika dla akcji
  const isMatchingPath = matchPath(
    "/contests/:contestId/applications/:applicationId/details",
    location.pathname,
  );
  const isMatchingPaymentsPath = matchPath(
    "/contests/:contestId/applications/:applicationId/paymentsDetails",
    location.pathname,
  );

  const handleUpdateStatus = async (status: ApplicationStatus) => {
    const result = await updateApplicationStatus(status);
    if (!result?.success) {
      setApproveError(result?.error || "Nie udało się zaktualizować statusu.");
      setLockApproval(true);
      return;
    }
    // Jeśli zmieniamy status na SENT (lub inny niż DRAFT), automatycznie ustaw switch na true
    if (status !== ApplicationStatus.DRAFT) {
      setShowOnlyWithPresentations(true);
    }
    refreshApplication(application?.userId as string, application?.id as string);
  };

  const handleGeneratePDF = async () => {
    if (!application || !contest || !categories || !user) {
      console.error("Brak wymaganych danych do generowania PDF");
      return;
    }

    try {
      await generateApplicationConfirmation(
        application,
        contest,
        categories,
        user,
        true,
        presentations,
      );
      setShowSuccessMessage(true);
    } catch (error) {
      console.error("Błąd podczas generowania PDF:", error);
    }
  };

  const registrationOpen = isRegistrationOpen(contest || undefined);

  const canApprove =
    !!application &&
    application.status === ApplicationStatus.DRAFT &&
    (presentations?.length ?? 0) > 0 &&
    registrationOpen &&
    !lockApproval;

  const getApproveTooltip = () => {
    if (!application) return "Brak zgłoszenia";
    if (application.status !== ApplicationStatus.DRAFT)
      return "Zgłoszenie nie jest w trybie edycji";
    if ((presentations?.length ?? 0) <= 0) return "Musisz dodać co najmniej jedną prezentację";
    return "";
  };

  const isPDFAvailable = (application: ApplicationWithId | null) => {
    if (!application) return false;
    return (
      application.status === ApplicationStatus.SENT ||
      application.status === ApplicationStatus.APPROVED
    );
  };

  const getPDFTooltip = () => {
    if (!application) return "Brak zgłoszenia";
    if ((presentations?.length ?? 0) <= 0) return "Musisz dodać co najmniej jedną prezentację";

    switch (application.status) {
      case ApplicationStatus.DRAFT:
        return "Potwierdzenie dostępne po wysłaniu zgłoszenia";
      case ApplicationStatus.MODERATION:
        return "Potwierdzenie dostępne po zakończeniu moderacji";
      case ApplicationStatus.CANCELED:
        return "Zgłoszenie zostało anulowane";
      case ApplicationStatus.SENT:
      case ApplicationStatus.APPROVED:
      case ApplicationStatus.REJECTED:
        return ""; // Brak tooltip - przycisk dostępny
      default:
        return "Potwierdzenie niedostępne w tym statusie";
    }
  };

  const actions = [
    {
      name: "Pokaż zgłoszenie",
      color: "primary" as const,
      icon: <Search />,
      onClick: () => setShowPayments(false),
      renderCondition: !!application && showPayments,
    },
    {
      name: "Pokaż opłaty",
      color: "warning" as const,
      icon: <MonetizationOn />,
      onClick: () => setShowPayments(true),
      disabled: false,
      renderCondition: !!application && !showPayments,
    },
    {
      name: "Zatwierdź zgłoszenie",
      color: "success" as const,
      icon: <Check />,
      onClick: () => handleUpdateStatus(ApplicationStatus.SENT),
      disabled: !canApprove,
      renderCondition:
        !!application &&
        application?.status === ApplicationStatus.DRAFT &&
        registrationOpen &&
        !lockApproval,
      tooltip: getApproveTooltip(),
    },
    {
      name: "Edytuj zgłoszenie",
      color: "warning" as const,
      icon: <Undo />,
      onClick: () => handleUpdateStatus(ApplicationStatus.DRAFT),
      disabled: application?.status !== ApplicationStatus.SENT,
      renderCondition:
        !!application && application?.status === ApplicationStatus.SENT && registrationOpen,
    },
    {
      name: isGenerating ? "Generowanie PDF..." : "Pobierz potwierdzenie",
      color: "primary" as const,
      icon: <PictureAsPdf />,
      onClick: handleGeneratePDF,
      disabled: isGenerating || !isPDFAvailable(application) || (presentations?.length ?? 0) <= 0,
      renderCondition:
        !!application && isPDFAvailable(application) && (presentations?.length ?? 0) > 0,
      tooltip: getPDFTooltip(),
    },
  ];

  if (!application) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 4, mt: 2 }}>
        <EmptyState message="Nie znaleziono zgłoszenia" />
      </Box>
    );
  }

  // Pokaż loading podczas ładowania prezentacji
  if (presentationsLoading) {
    return <Loading />;
  }

  // Oblicz ilość zgłoszonych zawodników
  const participantsCount = Object.keys(application.participantPresentationsMap || {}).length;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {/* Nagłówek strony */}
      <ApplicationHeader application={application} />

      {/* Informacje o zgłoszeniu z przyciskami akcji */}
      <Box
        sx={{
          border: "1px solid #ddd",
          borderRadius: 1,
          backgroundColor: "#fff",
        }}
      >
        <ApplicationInfoSection
          application={application}
          contest={contest || undefined}
          user={user || undefined}
          participantsCount={participantsCount}
          presentationsCount={presentations?.length || 0}
          onApplicationUpdate={async (applicationId, data) => {
            const result = await updateApplication(applicationId, data);
            if (result.success && application) {
              refreshApplication(application.userId, application.id);
            }
            return result;
          }}
        />

        {/* Przyciski akcji */}
        {(isMatchingPath || isMatchingPaymentsPath) && (
          <Box sx={{ p: 2, borderTop: "1px solid #eee" }}>
            <ApplicationActions actions={actions} />
          </Box>
        )}
      </Box>

      {/* Sekcja główna: kategorie lub płatności */}
      {showPayments ? (
        <ApplicationPaymentsPanel />
      ) : (
        <CategoriesList
          categories={paginatedCategories}
          application={application}
          presentations={filteredPresentations}
          searchTerm={searchTerm}
          allCollapsed={allCollapsed}
          selectedDays={selectedDays}
          onDayFilterChange={setSelectedDays}
          availableDays={availableDays}
          showOnlyWithPresentations={showOnlyWithPresentations}
          onShowOnlyWithPresentationsChange={setShowOnlyWithPresentations}
          showOnlyWithCustomMusic={showOnlyWithCustomMusic}
          onShowOnlyWithCustomMusicChange={() => {}} // TEMPORARILY DISABLED FOR PROD - Music functionality
          onAddPresentation={handleAddPresentation}
          onEditPresentation={handleEditPresentation}
          onDeletePresentation={handleDeletePresentation}
          onSearchChange={setSearchTerm}
          onToggleCollapse={handleCollapseAll}
          // Paginacja
          totalCategories={filteredCategories.length}
          currentPage={categoriesPage}
          itemsPerPage={categoriesPerPage}
          onPageChange={setCategoriesPage}
          onItemsPerPageChange={setCategoriesPerPage}
        />
      )}

      {/* Dialog potwierdzenia usunięcia prezentacji */}
      <DeletePresentationDialog
        open={deleteDialogOpen}
        presentation={presentationToDelete}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      {/* Powiadomienia o błędach PDF */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={clearError}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={clearError}>
          {error}
        </Alert>
      </Snackbar>

      {/* Powiadomienie o błędzie zatwierdzania */}
      <Snackbar
        open={!!approveError}
        autoHideDuration={6000}
        onClose={() => setApproveError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          sx={{ border: "1px solid #f44336" }}
          onClose={() => setApproveError(null)}
        >
          {approveError}
        </Alert>
      </Snackbar>

      {/* Powiadomienie o sukcesie PDF */}
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={4000}
        onClose={() => setShowSuccessMessage(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setShowSuccessMessage(false)}>
          Potwierdzenie zgłoszenia zostało pobrane!
        </Alert>
      </Snackbar>
    </Box>
  );
}
